'use client';

import { useLenis } from 'lenis/react';
import { AnimatePresence, LayoutGroup, motion, Transition } from 'motion/react';
import Image from 'next/image';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import slugify from 'slugify';
import { z } from 'zod';

import CallToAction from '~/components/atoms/CallToAction';
import Heading from '~/components/atoms/Heading';
import LayoutGrid from '~/components/molecules/LayoutGrid';
import WysiwygString from '~/components/molecules/WysiwygString';
import { schemas } from '~/components/organisms/slices/content-blocks/@types/schemas';
import { typenames } from '~/components/organisms/slices/content-blocks/@types/typenames';
import { ScrollArea, ScrollBar } from '~/components/ui/scroll-area';
import { useHydrationSafeIsClient } from '~/lib/hooks/useIsClient';
import { useSafeMailto } from '~/lib/hooks/useSafeMailto';
import { boolAttr, cn } from '~/lib/utils/classname';

export type TeamSliceClientProps = {
  teamMembers: NonNullable<z.infer<typeof schemas.teamLayout>['teamMembers']>;
  isPreview?: boolean;
};

const transition: Transition = {
  type: 'tween',
  duration: 0.8,
  ease: 'easeInOut',
};

export const TeamSliceClient: React.FC<TeamSliceClientProps> = ({
  teamMembers,
  isPreview,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentMemberIndex, setCurrentMemberIndex] = useState<number | null>(
    null
  );

  const [hasOverlayAnimation, setHasOverlayAnimation] =
    useState<boolean>(false);

  const isClient = useHydrationSafeIsClient();

  const currentMember =
    currentMemberIndex !== null ? teamMembers[currentMemberIndex] : null;

  const currentMemberKey =
    currentMemberIndex !== null && currentMember
      ? slugify([currentMember.fullName, currentMemberIndex].join('-'))
      : null;

  const mailtoCallback = useSafeMailto(currentMember?.contactEmail, {
    subject: 'Contact site Rivet & Vigreux',
  });

  const isPortalContentPresent = isOpen && currentMember && currentMemberKey;

  useLenis(
    (lenis) => {
      if (currentMemberIndex !== null || hasOverlayAnimation) {
        lenis.stop();
      } else {
        lenis.start();
      }
    },
    [currentMemberIndex, hasOverlayAnimation]
  );

  return (
    <LayoutGroup>
      <section className="mt-28 mb-20" data-slice={typenames.teamLayout}>
        {teamMembers && (
          <LayoutGrid
            innerAs="ul"
            className="group/team-list"
            classNames={{ inner: 'gap-y-20 max-lg:gap-x-4 max-lg:gap-y-8' }}
            data-open={boolAttr(currentMemberIndex !== null)}
          >
            {teamMembers.map((member, index) => {
              const key = slugify([member.fullName, index].join('-'));

              return (
                <li
                  key={key}
                  className={cn('col-span-1 flex lg:col-span-4', {
                    '-mt-6 lg:col-start-1 lg:mt-0': index % 4 === 0,
                    'mt-6 lg:col-start-6 lg:mt-0': index % 4 === 1,
                    '-mt-6 lg:col-start-4 lg:mt-0': index % 4 === 2,
                    'mt-6 lg:col-start-9 lg:mt-0': index % 4 === 3,
                  })}
                >
                  <button
                    type="button"
                    className={cn(
                      'flex w-full cursor-pointer flex-col gap-y-2 text-start transition-opacity duration-800 lg:gap-y-6',
                      {
                        'pointer-events-none z-40':
                          currentMemberIndex === index,
                      }
                    )}
                    onClick={() => {
                      if (!isPreview) {
                        setCurrentMemberIndex(index);
                        setIsOpen(true);
                      }
                    }}
                  >
                    {member.image && (
                      <motion.div
                        className="relative aspect-440/740 w-full"
                        layoutId={key}
                        transition={transition}
                        onLayoutAnimationStart={() =>
                          setHasOverlayAnimation(true)
                        }
                        onLayoutAnimationComplete={() => {
                          setHasOverlayAnimation(false);

                          if (!isOpen) {
                            setCurrentMemberIndex(null);
                          }
                        }}
                      >
                        <Image
                          src={member.image.node.sourceUrl}
                          alt={member.image.node.altText}
                          sizes={member.image.node.sizes}
                          placeholder={
                            member.image.node.blurDataUrl ? 'blur' : 'empty'
                          }
                          blurDataURL={
                            member.image.node.blurDataUrl || undefined
                          }
                          className="object-cover"
                          fill
                          unoptimized
                        />
                      </motion.div>
                    )}

                    <div className="flex flex-col gap-y-2 self-start transition-opacity duration-800 group-data-open/team-list:opacity-0">
                      <Heading
                        as="h2"
                        level="4xl"
                        className="text-brand-primary"
                      >
                        {member.fullName}
                      </Heading>

                      {member.jobTitle && (
                        <p className="text-brand-primary/40 text-sm">
                          {member.jobTitle}
                        </p>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </LayoutGrid>
        )}
      </section>

      {isClient &&
        !isPreview &&
        createPortal(
          <div className="pointer-events-none fixed top-0 left-0 z-30 h-full w-full">
            <AnimatePresence mode="wait">
              {isPortalContentPresent && (
                <motion.div
                  key={currentMemberKey}
                  className="pointer-events-auto relative h-full w-full py-20 pb-4 text-white lg:pt-4"
                >
                  <motion.div
                    className="bg-brand-secondary absolute top-0 left-0 z-0 h-full w-full"
                    initial={{ y: '-100%' }}
                    animate={{ y: '0%' }}
                    exit={{ y: '100%' }}
                    transition={transition}
                  />

                  <LayoutGrid
                    className="z-10 h-full"
                    classNames={{
                      inner:
                        'h-full items-center max-lg:grid-rows-[auto_1fr] max-lg:gap-x-4 max-lg:gap-y-8 gap-y-10',
                    }}
                  >
                    {currentMember.image && (
                      <motion.div
                        className="relative col-span-1 col-start-1 aspect-440/740 w-full lg:col-span-4"
                        layoutId={currentMemberKey}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={transition}
                      >
                        <Image
                          src={currentMember.image.node.sourceUrl}
                          alt={currentMember.image.node.altText}
                          sizes={currentMember.image.node.sizes}
                          placeholder={
                            currentMember.image.node.blurDataUrl
                              ? 'blur'
                              : 'empty'
                          }
                          blurDataURL={
                            currentMember.image.node.blurDataUrl || undefined
                          }
                          className="object-cover"
                          fill
                          unoptimized
                        />

                        <span className="font-heading pointer-events-none absolute top-0 left-0 grid h-full w-full place-items-center text-[200px] text-white mix-blend-soft-light select-none lg:text-[400px]">
                          {currentMember.fullName.trim().slice(0, 1)}
                        </span>
                      </motion.div>
                    )}

                    <motion.div
                      className="relative col-span-2 col-start-1 contents flex-col items-start gap-y-10 lg:col-span-4 lg:col-start-7 lg:flex lg:gap-y-16"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={transition}
                    >
                      <motion.div
                        className="col-span-1 col-start-2 row-start-1 flex flex-col justify-end gap-y-10 self-end lg:contents"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={transition}
                      >
                        <button
                          type="button"
                          className="-m-2 cursor-pointer self-start p-2"
                          onClick={() => setIsOpen(false)}
                        >
                          Retour
                        </button>

                        <div className="flex flex-col gap-y-2">
                          <Heading as="h2" level="4xl">
                            {currentMember.fullName}
                          </Heading>

                          {currentMember.jobTitle && (
                            <p className="text-sm text-white/40">
                              {currentMember.jobTitle}
                            </p>
                          )}
                        </div>
                      </motion.div>

                      <ScrollArea
                        className="col-span-2 col-start-1 h-full overflow-hidden lg:contents"
                        classNames={{ inner: 'pb-10' }}
                        data-prevent="lenis"
                      >
                        <motion.div
                          className="flex flex-col gap-y-8"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={transition}
                        >
                          {currentMember.biography && (
                            <div className="prose max-lg:prose-sm prose-invert relative leading-5! text-white">
                              <WysiwygString html={currentMember.biography} />
                            </div>
                          )}

                          {(currentMember.contactEmail ||
                            currentMember.linkedinLink) && (
                            <div className="relative flex flex-row flex-wrap gap-x-6 gap-y-3">
                              {currentMember.contactEmail && (
                                <CallToAction
                                  as="button"
                                  type="button"
                                  level={2}
                                  variant="dark"
                                  onClick={mailtoCallback}
                                >
                                  Contact
                                </CallToAction>
                              )}

                              {currentMember.linkedinLink && (
                                <CallToAction
                                  as="a"
                                  href={currentMember.linkedinLink.url}
                                  target="_blank"
                                  level={2}
                                  variant="dark"
                                >
                                  {currentMember.linkedinLink.title ||
                                    'LinkedIn'}
                                </CallToAction>
                              )}
                            </div>
                          )}
                        </motion.div>

                        <ScrollBar />
                      </ScrollArea>
                    </motion.div>
                  </LayoutGrid>
                </motion.div>
              )}
            </AnimatePresence>
          </div>,
          document.body
        )}
    </LayoutGroup>
  );
};
